/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useMemo } from 'react';
import { Lock as LockIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { valuationPathByDateAndVersion } from 'common/config/urls';
import { Alert, MessageBox } from 'components';
import { ConfirmationDialog } from 'components/Dialogs';
import DialogContext from 'context/DialogContext';
import { ValuationsParams } from 'pages/ValuationsAllocation/types';
import { Action, AllocationFinalMessageProps } from './AllocationFinalMessage.props';
import GoToCapAllocations from './GoToCapAllocations';
import ListAllocations from './ListAllocations';
import UnlockConfirmation from './UnlockConfirmation';

const AllocationFinalMessage = (props: AllocationFinalMessageProps) => {
  const {
    tableTerms,
    setIsDisabled,
    finalAllocations,
    savesAsProformaConfirmation,
    actions = [],
    variant,
    children,
    DocumentsDisabled,
    singleAlert,
    isAllocation,
    viewOnlyUser,
  } = props;
  const navigate = useNavigate();
  const { firmSlugParam, companySlugParam } = useParams<ValuationsParams>();
  const { showDialog } = useContext(DialogContext);

  const unlockCapTable = () => {
    setIsDisabled(false);
    if (DocumentsDisabled) {
      DocumentsDisabled(false);
    }
  };

  const manyFinals = useMemo(() => finalAllocations?.length > 1, [finalAllocations]);
  const snBoxMessage = 'an allocation marked as final.';
  const plBoxMessage = 'multiple allocations marked as final.';

  const unlockConfirmation = (showDuplicate = true) => {
    const dialogActions: Action[] = [
      {
        label: 'Unlock',
        variant: 'text',
        type: 'primary',
        callback: unlockCapTable,
      },
    ];

    if (showDuplicate) {
      actions.push({
        label: 'Duplicate',
        variant: 'contained',
        type: 'success',
        callback: savesAsProformaConfirmation,
      });
    }

    showDialog({
      wrapper: ConfirmationDialog,
      wrapperProps: {
        title: 'Confirmation',
      },
      content: UnlockConfirmation,
      actions: dialogActions,
    });
  };

  const alertActions = useMemo<Action[]>(
    () => [
      {
        label: 'Unlock',
        buttonProps: {
          variant: 'text',
          size: 'small',
          disabled: viewOnlyUser,
        },
        callback: unlockConfirmation,
      },
      ...(actions ?? []),
    ],
    [actions, viewOnlyUser]
  );

  const singleAlertActions = useMemo<Action[]>(
    () => [
      {
        label: 'Unlock',
        buttonProps: {
          variant: 'text',
          size: 'small',
          disabled: viewOnlyUser,
        },
        callback: () => unlockConfirmation(false),
      },
    ],
    [viewOnlyUser]
  );

  const handleClick = (dateSlug: string, versionSlug: string) => {
    if (firmSlugParam && companySlugParam && dateSlug && versionSlug) {
      navigate(valuationPathByDateAndVersion(firmSlugParam, companySlugParam, dateSlug, versionSlug));
    }
  };

  const tableBeingUsedOnMsg = useMemo(() => {
    const tableNameIsBeingUsedOn = `This ${tableTerms.tableName} is being used on`;
    const snAlertMessage = 'a final allocation. Any changes can alter its data.';
    const plAlertMessage = 'several final allocations. Any changes can alter their data.';
    return manyFinals ? `${tableNameIsBeingUsedOn} ${plAlertMessage}` : `${tableNameIsBeingUsedOn} ${snAlertMessage}`;
  }, [tableTerms, manyFinals]);

  const alertMessage = useMemo(
    () =>
      isAllocation ? 'This allocation table is marked as Final, any change can alter this data.' : tableBeingUsedOnMsg,
    [tableTerms, isAllocation, manyFinals]
  );

  if (singleAlert) {
    return (
      <Alert isAlertVisible icon={<LockIcon fontSize="small" />} actions={singleAlertActions}>
        {alertMessage}
      </Alert>
    );
  }

  return variant ? (
    <Alert isAlertVisible icon={<LockIcon fontSize="small" />} actions={alertActions}>
      {alertMessage}
    </Alert>
  ) : (
    <>
      <Alert isAlertVisible icon={<LockIcon fontSize="small" />} actions={alertActions}>
        {tableBeingUsedOnMsg}
      </Alert>
      <MessageBox
        title="Final Allocation associated"
        tagline={`This ${tableTerms.tableName} is being used on ${manyFinals ? plBoxMessage : snBoxMessage}`}
        fullWidth={false}
        action={
          manyFinals ? (
            <ListAllocations finalAllocations={finalAllocations} handleClick={handleClick} />
          ) : (
            <GoToCapAllocations finalAllocations={finalAllocations} handleClick={handleClick} />
          )
        }
      />
      {children}
    </>
  );
};

export default AllocationFinalMessage;
